import { useState, useEffect } from 'react';
import { Link } from 'wouter';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Settings, Plus, Edit3, Trash2, Save, ArrowLeft, Home, Shield, DollarSign, Music, Calculator, TrendingUp, RefreshCw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import Navbar from '@/components/navbar';
import type { SetCorePricing as GaPricing, InsertSetCorePricing as InsertGaPricing, SetAudioPricing as VipPricing, InsertSetAudioPricing as InsertVipPricing, SetVideoPricing as AllAccessPricing, InsertSetVideoPricing as InsertAllAccessPricing } from '@shared/schema';
import {
  calculatePackagePrice,
  calculateAllPackages,
  calculateAllPackagesWithDatabasePricing as calculateAllPackagesWithGaPricing,
  calculateSetCorePriceFromTable as calculateGaPriceFromTable,
  calculateSetAudioPriceFromTable as calculateVipPriceFromTable,
  calculateSetVideoPriceFromTable as calculateAllAccessPriceFromTable,
  defaultPricingConfigs,
  defaultPackageConfigs,
  type CalculationResult,
  type PricingConfig,
  type SetCorePricingData as GaPricingData,
  type SetAudioPricingData as VipPricingData,
  type SetVideoPricingData as AllAccessPricingData,
} from '../../../shared/pricing-calculator';


interface MediaObjectiveSettings {
  awareness: {
    cpm: number;
    impressionMultipliers: {
      small: number;
      medium: number;
      large: number;
      nationalTour: number;
    };
  };
  engagement: {
    baseCPM: number;
    marketMultipliers: {
      small: number;
      medium: number;
      large: number;
      nationalTour: number;
    };
  };
  leads: {
    baseCPM: number;
    marketMultipliers: {
      small: number;
      medium: number;
      large: number;
      nationalTour: number;
    };
    leadDivisors: {
      low: Record<string, number>;
      high: Record<string, number>;
    };
  };
}

interface VolumeConfig {
  stage: string;
  baseCogs: number;
  exponent: number;
  minCostGA: number;
  minCostVIP: number;
  minCostAllAccess: number;
}

export default function AdminCalculatorSettings() {
  const [activeTab, setActiveTab] = useState<string>('ga-pricing');
  const [editingGaPricing, setEditingGaPricing] = useState<string | null>(null);
  const [editingGaValues, setEditingGaValues] = useState<Partial<GaPricing>>({});
  const [editingVipPricing, setEditingVipPricing] = useState<string | null>(null);
  const [editingVipValues, setEditingVipValues] = useState<Partial<VipPricing>>({});
  const [editingAllAccessPricing, setEditingAllAccessPricing] = useState<string | null>(null);
  const [editingAllAccessValues, setEditingAllAccessValues] = useState<Partial<AllAccessPricing>>({});

  // Test calculator state
  const [testCareerStage, setTestCareerStage] = useState("Emerging");
  const [testNumberOfShows, setTestNumberOfShows] = useState(1);
  const [testSelectedPackage, setTestSelectedPackage] = useState<'GA' | 'VIP' | 'All-Access'>('GA');
  const [testResults, setTestResults] = useState<Record<string, CalculationResult> | null>(null);
  const [testSingleResult, setTestSingleResult] = useState<CalculationResult | null>(null);
  const [testActualConfigs, setTestActualConfigs] = useState<PricingConfig[]>(defaultPricingConfigs);
  const [testUsingSavedValues, setTestUsingSavedValues] = useState(false);

  const { toast } = useToast();
  const queryClient = useQueryClient();

  // No longer loading from localStorage - using database values only

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Removed artist_cost_pricing query - table deprecated and replaced by GA/VIP/All-Access tables

  // Fetch GA pricing data
  const { data: gaPricingData = [], isLoading: gaPricingLoading } = useQuery<GaPricing[]>({
    queryKey: ['/api/set-core-pricing'],
    queryFn: () => apiRequest('/api/set-core-pricing'),
  });

  // Mutation for updating GA pricing
  const updateGaPricingMutation = useMutation({
    mutationFn: async ({ careerStage, data }: { careerStage: string; data: Partial<InsertGaPricing> }) => {
      return await apiRequest(`/api/set-core-pricing/${encodeURIComponent(careerStage)}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/set-core-pricing'] });
      toast({
        title: "SET Core Pricing Updated",
        description: "The SET Core pricing has been saved successfully.",
      });
    },
    onError: () => {
      toast({
        title: "Update Failed",
        description: "Failed to update SET Core pricing. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Fetch VIP pricing data
  const { data: vipPricingData = [], isLoading: vipPricingLoading } = useQuery<VipPricing[]>({
    queryKey: ['/api/set-audio-pricing'],
    queryFn: () => apiRequest('/api/set-audio-pricing'),
  });

  // Fetch All-Access pricing data
  const { data: allAccessPricingData = [], isLoading: allAccessPricingLoading } = useQuery<AllAccessPricing[]>({
    queryKey: ['/api/set-video-pricing'],
    queryFn: () => apiRequest('/api/set-video-pricing'),
  });

  // Test calculator functions - now using database pricing tables
  // Get all unique career stages from all three pricing tables
  // Normalize "Developing/Local" to "Developing" for consistency
  const normalizeCareerStage = (stage: string) => {
    if (stage.toLowerCase().includes('developing') || stage.toLowerCase().includes('local')) {
      return 'Developing';
    }
    return stage;
  };

  const testCareerStages = Array.from(new Set([
    ...gaPricingData.map(p => normalizeCareerStage(p.careerStage)),
    ...vipPricingData.map(p => normalizeCareerStage(p.careerStage)),
    ...allAccessPricingData.map(p => normalizeCareerStage(p.careerStage))
  ])).sort();

  const testCurrentConfig = testActualConfigs.find(c => c.careerStage === testCareerStage);

  const handleTestCalculateAll = () => {
    // Find pricing data for this career stage from database
    // Handle "Developing" -> "Developing/Local" mapping for GA pricing
    const gaData = gaPricingData.find(p => {
      const normalized = normalizeCareerStage(p.careerStage);
      return normalized === testCareerStage;
    });
    const vipData = vipPricingData.find(p => {
      const normalized = normalizeCareerStage(p.careerStage);
      return normalized === testCareerStage;
    });
    const allAccessData = allAccessPricingData.find(p => {
      const normalized = normalizeCareerStage(p.careerStage);
      return normalized === testCareerStage;
    });

    // Convert to the format expected by calculator
    const gaPricingForCalc: GaPricingData | null = gaData ? {
      careerStage: gaData.careerStage,
      avgCapacity: gaData.avgCapacity,
      price1Show: gaData.price1Show,
      price2Shows: gaData.price2Shows,
      price3Shows: gaData.price3Shows,
      price4Shows: gaData.price4Shows,
      price5PlusShows: gaData.price5PlusShows,
    } : null;

    const vipPricingForCalc: VipPricingData | null = vipData ? {
      careerStage: vipData.careerStage,
      avgCapacity: vipData.avgCapacity,
      artistCogs: vipData.artistCogs,
      exponent: vipData.exponent,
      packageType: vipData.packageType,
      packageMarkup: vipData.packageMarkup,
      minCostPerShow: vipData.minCostPerShow,
    } : null;

    const allAccessPricingForCalc: AllAccessPricingData | null = allAccessData ? {
      careerStage: allAccessData.careerStage,
      avgCapacity: allAccessData.avgCapacity,
      artistCogs: allAccessData.artistCogs,
      exponent: allAccessData.exponent,
      packageType: allAccessData.packageType,
      packageMarkup: allAccessData.packageMarkup,
      minCostPerShow: allAccessData.minCostPerShow,
    } : null;

    // Use new database-aware calculator
    const allResults = calculateAllPackagesWithGaPricing(
      testCareerStage,
      testNumberOfShows,
      gaPricingForCalc,
      testCurrentConfig,
      vipPricingForCalc,
      allAccessPricingForCalc
    );

    setTestResults(allResults);
    setTestSingleResult(null);
  };

  const handleTestCalculateSingle = () => {
    // Find pricing data for this career stage based on selected package
    if (testSelectedPackage === 'GA') {
      const gaData = gaPricingData.find(p => {
        const normalized = normalizeCareerStage(p.careerStage);
        return normalized === testCareerStage;
      });
      if (!gaData) {
        toast({
          title: "Pricing Not Found",
          description: `No GA pricing found for ${testCareerStage}`,
          variant: "destructive",
        });
        return;
      }

      const gaPricingForCalc: GaPricingData = {
        careerStage: gaData.careerStage,
        avgCapacity: gaData.avgCapacity,
        price1Show: gaData.price1Show,
        price2Shows: gaData.price2Shows,
        price3Shows: gaData.price3Shows,
        price4Shows: gaData.price4Shows,
        price5PlusShows: gaData.price5PlusShows,
      };

      const result = calculateGaPriceFromTable(gaPricingForCalc, testNumberOfShows);
      setTestSingleResult(result);
      setTestResults(null);

    } else if (testSelectedPackage === 'VIP') {
      const vipData = vipPricingData.find(p => {
        const normalized = normalizeCareerStage(p.careerStage);
        return normalized === testCareerStage;
      });
      if (!vipData) {
        toast({
          title: "Pricing Not Found",
          description: `No VIP pricing found for ${testCareerStage}`,
          variant: "destructive",
        });
        return;
      }

      const vipPricingForCalc: VipPricingData = {
        careerStage: vipData.careerStage,
        avgCapacity: vipData.avgCapacity,
        artistCogs: vipData.artistCogs,
        exponent: vipData.exponent,
        packageType: vipData.packageType,
        packageMarkup: vipData.packageMarkup,
        minCostPerShow: vipData.minCostPerShow,
      };

      const result = calculateVipPriceFromTable(vipPricingForCalc, testNumberOfShows);
      setTestSingleResult(result);
      setTestResults(null);

    } else if (testSelectedPackage === 'All-Access') {
      const allAccessData = allAccessPricingData.find(p => {
        const normalized = normalizeCareerStage(p.careerStage);
        return normalized === testCareerStage;
      });
      if (!allAccessData) {
        toast({
          title: "Pricing Not Found",
          description: `No All-Access pricing found for ${testCareerStage}`,
          variant: "destructive",
        });
        return;
      }

      const allAccessPricingForCalc: AllAccessPricingData = {
        careerStage: allAccessData.careerStage,
        avgCapacity: allAccessData.avgCapacity,
        artistCogs: allAccessData.artistCogs,
        exponent: allAccessData.exponent,
        packageType: allAccessData.packageType,
        packageMarkup: allAccessData.packageMarkup,
        minCostPerShow: allAccessData.minCostPerShow,
      };

      const result = calculateAllAccessPriceFromTable(allAccessPricingForCalc, testNumberOfShows);
      setTestSingleResult(result);
      setTestResults(null);
    }
  };

  // Mutation for updating VIP pricing
  const updateVipPricingMutation = useMutation({
    mutationFn: async ({ careerStage, data }: { careerStage: string; data: Partial<InsertVipPricing> }) => {
      return await apiRequest(`/api/set-audio-pricing/${encodeURIComponent(careerStage)}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/set-audio-pricing'] });
      toast({
        title: "SET Audio Pricing Updated",
        description: "The SET Audio pricing has been saved successfully.",
      });
    },
    onError: () => {
      toast({
        title: "Update Failed",
        description: "Failed to update SET Audio pricing. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Mutation for updating All-Access pricing
  const updateAllAccessPricingMutation = useMutation({
    mutationFn: async ({ careerStage, data }: { careerStage: string; data: Partial<InsertAllAccessPricing> }) => {
      return await apiRequest(`/api/set-video-pricing/${encodeURIComponent(careerStage)}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/set-video-pricing'] });
      toast({
        title: "SET Video Pricing Updated",
        description: "The SET Video pricing has been saved successfully.",
      });
    },
    onError: () => {
      toast({
        title: "Update Failed",
        description: "Failed to update SET Video pricing. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Default settings structure
  const [mediaSettings, setMediaSettings] = useState<MediaObjectiveSettings>({
    awareness: {
      cpm: 2.50,
      impressionMultipliers: {
        small: 1500000,
        medium: 3000000,
        large: 6000000,
        nationalTour: 2200000
      }
    },
    engagement: {
      baseCPM: 10,
      marketMultipliers: {
        small: 1.2,
        medium: 1.0,
        large: 0.96,
        nationalTour: 1.0
      }
    },
    leads: {
      baseCPM: 25,
      marketMultipliers: {
        small: 1.2,
        medium: 1.0,
        large: 0.96,
        nationalTour: 1.0
      },
      leadDivisors: {
        low: {
          'Mainstream 4': 40,
          'Mainstream 3': 35,
          'Mainstream 2': 30,
          'Mainstream 1': 25,
          'Established 3': 25,
          'Established 2': 28,
          'Established 1': 30,
          'Emerging': 30
        },
        high: {
          'Mainstream 4': 125,
          'Mainstream 3': 100,
          'Mainstream 2': 90,
          'Mainstream 1': 80,
          'Established 3': 80,
          'Established 2': 85,
          'Established 1': 100,
          'Emerging': 30
        }
      }
    }
  });




  const updateMediaSetting = (path: string, value: number) => {
    setMediaSettings(prev => {
      const newSettings = { ...prev };
      const keys = path.split('.');
      let current: any = newSettings;
      
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newSettings;
    });
  };

  // GA Pricing edit functions
  const handleEditGaPricing = (careerStage: string, pricing: GaPricing) => {
    setEditingGaPricing(careerStage);
    setEditingGaValues(pricing);
  };

  const handleSaveGaPricing = async () => {
    if (!editingGaPricing || !editingGaValues) return;

    await updateGaPricingMutation.mutateAsync({
      careerStage: editingGaPricing,
      data: {
        avgCapacity: editingGaValues.avgCapacity,
        price1Show: editingGaValues.price1Show,
        price2Shows: editingGaValues.price2Shows,
        price3Shows: editingGaValues.price3Shows,
        price4Shows: editingGaValues.price4Shows,
        price5PlusShows: editingGaValues.price5PlusShows,
      },
    });

    setEditingGaPricing(null);
    setEditingGaValues({});
  };

  const handleCancelGaEdit = () => {
    setEditingGaPricing(null);
    setEditingGaValues({});
  };

  const updateGaEditingValue = (field: keyof GaPricing, value: number) => {
    setEditingGaValues(prev => ({ ...prev, [field]: value }));
  };

  // VIP Pricing edit functions
  const handleEditVipPricing = (careerStage: string, pricing: VipPricing) => {
    setEditingVipPricing(careerStage);
    setEditingVipValues(pricing);
  };

  const handleSaveVipPricing = async () => {
    if (!editingVipPricing || !editingVipValues) return;

    await updateVipPricingMutation.mutateAsync({
      careerStage: editingVipPricing,
      data: {
        avgCapacity: editingVipValues.avgCapacity,
        artistCogs: editingVipValues.artistCogs,
        exponent: editingVipValues.exponent,
        packageMarkup: editingVipValues.packageMarkup,
        minCostPerShow: editingVipValues.minCostPerShow,
      },
    });

    setEditingVipPricing(null);
    setEditingVipValues({});
  };

  const handleCancelVipEdit = () => {
    setEditingVipPricing(null);
    setEditingVipValues({});
  };

  const updateVipEditingValue = (field: keyof VipPricing, value: number | string) => {
    setEditingVipValues(prev => ({ ...prev, [field]: value }));
  };

  // All-Access Pricing edit functions
  const handleEditAllAccessPricing = (careerStage: string, pricing: AllAccessPricing) => {
    setEditingAllAccessPricing(careerStage);
    setEditingAllAccessValues(pricing);
  };

  const handleSaveAllAccessPricing = async () => {
    if (!editingAllAccessPricing || !editingAllAccessValues) return;

    await updateAllAccessPricingMutation.mutateAsync({
      careerStage: editingAllAccessPricing,
      data: {
        avgCapacity: editingAllAccessValues.avgCapacity,
        artistCogs: editingAllAccessValues.artistCogs,
        exponent: editingAllAccessValues.exponent,
        packageMarkup: editingAllAccessValues.packageMarkup,
        minCostPerShow: editingAllAccessValues.minCostPerShow,
      },
    });

    setEditingAllAccessPricing(null);
    setEditingAllAccessValues({});
  };

  const handleCancelAllAccessEdit = () => {
    setEditingAllAccessPricing(null);
    setEditingAllAccessValues({});
  };

  const updateAllAccessEditingValue = (field: keyof AllAccessPricing, value: number | string) => {
    setEditingAllAccessValues(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-6xl mx-auto p-6 space-y-6 pt-24">
        {/* Navigation Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-6">
            <Link href="/">
              <Button variant="outline" size="sm" className="flex items-center gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back to Home
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <Link href="/">
                <Button variant="ghost" size="sm" className="flex items-center gap-2">
                  <Home className="h-4 w-4" />
                  Home
                </Button>
              </Link>
              <Link href="/admin/profile">
                <Button variant="ghost" size="sm" className="flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Admin Profile
                </Button>
              </Link>
              <Link href="/admin/filters">
                <Button variant="ghost" size="sm" className="flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Filter Management
                </Button>
              </Link>
              <Link href="/admin/venues">
                <Button variant="ghost" size="sm" className="flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Venue Management
                </Button>
              </Link>
            </div>
          </div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Settings className="h-8 w-8" />
            Calculator Settings
          </h1>
          <p className="text-gray-600 mt-2">
            Configure pricing formulas, volume discounts, and minimum costs for sponsorship packages
          </p>
        </div>

      <div className="flex gap-4 mb-6">
        <Button
          variant={activeTab === 'ga-pricing' ? 'default' : 'outline'}
          onClick={() => setActiveTab('ga-pricing')}
        >
          SET Core Pricing
        </Button>
        <Button
          variant={activeTab === 'vip-pricing' ? 'default' : 'outline'}
          onClick={() => setActiveTab('vip-pricing')}
        >
          SET Audio Pricing
        </Button>
        <Button
          variant={activeTab === 'allaccess-pricing' ? 'default' : 'outline'}
          onClick={() => setActiveTab('allaccess-pricing')}
        >
          SET Video Pricing
        </Button>
        <Button
          variant={activeTab === 'test-calculator' ? 'default' : 'outline'}
          onClick={() => setActiveTab('test-calculator')}
        >
          Test Calculator
        </Button>
      </div>

      {activeTab === 'test-calculator' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Input Controls */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calculator className="h-5 w-5" />
                Test Parameters
              </CardTitle>
              <CardDescription>
                Configure the test parameters to validate pricing calculations {testUsingSavedValues ? '(using saved admin values)' : '(using default values)'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="test-career-stage">Career Stage</Label>
                <Select value={testCareerStage} onValueChange={setTestCareerStage}>
                  <SelectTrigger id="test-career-stage">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {testCareerStages.map(stage => (
                      <SelectItem key={stage} value={stage}>{stage}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="test-num-shows">Number of Shows</Label>
                <Input
                  id="test-num-shows"
                  type="number"
                  min="1"
                  max="50"
                  value={testNumberOfShows}
                  onChange={(e) => setTestNumberOfShows(parseInt(e.target.value) || 1)}
                />
              </div>

              <div>
                <Label htmlFor="test-package-type">Package Type (for single calculation)</Label>
                <Select value={testSelectedPackage} onValueChange={(value) => setTestSelectedPackage(value as any)}>
                  <SelectTrigger id="test-package-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GA">SET Core Package</SelectItem>
                    <SelectItem value="VIP">SET Audio Package</SelectItem>
                    <SelectItem value="All-Access">SET Video Package</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-2 pt-4">
                <Button onClick={handleTestCalculateAll} className="flex-1">
                  Calculate All Packages
                </Button>
                <Button onClick={handleTestCalculateSingle} variant="outline" className="flex-1">
                  Calculate {testSelectedPackage}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Current Configuration - Database Values */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Current Database Configuration
                <Badge variant="default">Database Values</Badge>
              </CardTitle>
              <CardDescription>
                Pricing configuration for {testCareerStage}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* GA Pricing from Database */}
                {(() => {
                  const gaData = gaPricingData.find(p => {
                    const normalized = normalizeCareerStage(p.careerStage);
                    return normalized === testCareerStage;
                  });
                  if (gaData) {
                    return (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <h4 className="text-sm font-semibold text-green-900 mb-2">SET Core Package (Database)</h4>
                        <div className="space-y-1 text-xs">
                          <div className="flex justify-between">
                            <span>1 Show:</span>
                            <span className="font-mono">{formatCurrency(gaData.price1Show / 100)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>2 Shows:</span>
                            <span className="font-mono">{formatCurrency(gaData.price2Shows / 100)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>3 Shows:</span>
                            <span className="font-mono">{formatCurrency(gaData.price3Shows / 100)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>4 Shows:</span>
                            <span className="font-mono">{formatCurrency(gaData.price4Shows / 100)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>5+ Shows:</span>
                            <span className="font-mono">{formatCurrency(gaData.price5PlusShows / 100)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return <div className="text-sm text-gray-500">No SET Core pricing data for this career stage</div>;
                })()}

                {/* VIP Pricing from Database */}
                {(() => {
                  const vipData = vipPricingData.find(p => {
                    const normalized = normalizeCareerStage(p.careerStage);
                    return normalized === testCareerStage;
                  });
                  if (vipData) {
                    return (
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                        <h4 className="text-sm font-semibold text-purple-900 mb-2">SET Audio Package (Database)</h4>
                        <div className="space-y-1 text-xs">
                          <div className="flex justify-between">
                            <span>Artist COGS:</span>
                            <span className="font-mono">{formatCurrency(vipData.artistCogs / 100)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Exponent:</span>
                            <span className="font-mono">{vipData.exponent}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Package Markup:</span>
                            <span className="font-mono">{vipData.packageMarkup}×</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Min Cost/Show:</span>
                            <span className="font-mono">{formatCurrency(vipData.minCostPerShow / 100)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return <div className="text-sm text-gray-500">No SET Audio pricing data for this career stage</div>;
                })()}

                {/* All-Access Pricing from Database */}
                {(() => {
                  const allAccessData = allAccessPricingData.find(p => {
                    const normalized = normalizeCareerStage(p.careerStage);
                    return normalized === testCareerStage;
                  });
                  if (allAccessData) {
                    return (
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                        <h4 className="text-sm font-semibold text-orange-900 mb-2">SET Video Package (Database)</h4>
                        <div className="space-y-1 text-xs">
                          <div className="flex justify-between">
                            <span>Artist COGS:</span>
                            <span className="font-mono">{formatCurrency(allAccessData.artistCogs / 100)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Exponent:</span>
                            <span className="font-mono">{allAccessData.exponent}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Package Markup:</span>
                            <span className="font-mono">{allAccessData.packageMarkup}×</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Min Cost/Show:</span>
                            <span className="font-mono">{formatCurrency(allAccessData.minCostPerShow / 100)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return <div className="text-sm text-gray-500">No SET Video pricing data for this career stage</div>;
                })()}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Results */}
      {activeTab === 'test-calculator' && (testResults || testSingleResult) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Calculation Results
            </CardTitle>
            <CardDescription>
              {testNumberOfShows === 1 ? 'Single show pricing' : `Volume pricing for ${testNumberOfShows} shows`} 
              {' '}for {testCareerStage} career stage
            </CardDescription>
          </CardHeader>
          <CardContent>
            {testSingleResult && (
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2">{testSelectedPackage} Package Result</h4>
                  <div className="space-y-2 text-sm">
                    <div><strong>Formula:</strong> {testSingleResult.formula}</div>
                    <div><strong>Final Price:</strong> {formatCurrency(testSingleResult.finalPrice)}</div>
                    {testSingleResult.minCostApplied && (
                      <Badge variant="secondary">Minimum cost applied</Badge>
                    )}
                  </div>
                </div>
              </div>
            )}

            {testResults && (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Package</TableHead>
                      <TableHead>Formula</TableHead>
                      <TableHead>Final Price</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.entries(testResults).map(([packageType, result]) => (
                      <TableRow key={packageType}>
                        <TableCell className="font-medium">{packageType}</TableCell>
                        <TableCell className="font-mono text-xs max-w-xs">{result.formula}</TableCell>
                        <TableCell className="font-bold text-green-600">
                          {formatCurrency(result.finalPrice)}
                        </TableCell>
                        <TableCell>
                          {result.minCostApplied ? (
                            <Badge variant="secondary">Min Applied</Badge>
                          ) : (
                            <Badge variant="default">Volume</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            <div className="mt-4 p-4 bg-amber-50 rounded-lg">
              <h4 className="font-medium text-amber-900 mb-2">Pricing Formulas</h4>
              <ul className="text-sm text-amber-800 space-y-1">
                <li>• <strong>Single Show:</strong> Base COGS × Package Multiplier</li>
                <li>• <strong>Multiple Shows:</strong> (Base COGS × # shows^exponent) × Package Multiplier</li>
                <li>• <strong>Package Multipliers:</strong> SET Core: 0.3×, SET Audio: 1.5×, SET Video: 2.875×</li>
                <li>• <strong>Minimum Enforcement:</strong> Final price cannot be below minimum cost per show × number of shows</li>
                <li>• <strong>Volume Discount:</strong> Exponent reduces per-show cost as show count increases</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Removed pricing-config tab - now using database-driven pricing tables (GA, VIP, All-Access) */}

      {activeTab === 'ga-pricing' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              SET Core Pricing Table
            </CardTitle>
            <CardDescription>
              Fixed pricing for SET Core packages based on number of shows. Volume discounts bottom out at 5+ shows.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {gaPricingLoading ? (
              <div className="animate-pulse space-y-3">
                <div className="h-10 bg-gray-200 rounded"></div>
                <div className="h-10 bg-gray-200 rounded"></div>
                <div className="h-10 bg-gray-200 rounded"></div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-2">SET Core Pricing Structure</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• <strong>Fixed Pricing:</strong> SET Core packages use fixed per-show pricing (no volume formula)</li>
                    <li>• <strong>Volume Discounts:</strong> Price per show decreases as show count increases</li>
                    <li>• <strong>Minimum Price:</strong> 5+ shows all use the same (lowest) per-show price</li>
                    <li>• <strong>All prices are per show</strong> - multiply by number of shows for total</li>
                  </ul>
                </div>

                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[180px]">Career Stage</TableHead>
                        <TableHead className="text-center">Avg Capacity</TableHead>
                        <TableHead className="text-center bg-green-50">1 Show</TableHead>
                        <TableHead className="text-center bg-green-100">2 Shows</TableHead>
                        <TableHead className="text-center bg-green-200">3 Shows</TableHead>
                        <TableHead className="text-center bg-green-300">4 Shows</TableHead>
                        <TableHead className="text-center bg-green-400">5+ Shows</TableHead>
                        <TableHead className="text-center w-[100px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {gaPricingData.map((pricing) => {
                        const isEditing = editingGaPricing === pricing.careerStage;
                        const currentValues = isEditing ? editingGaValues : pricing;

                        return (
                          <TableRow key={pricing.careerStage} className={isEditing ? 'bg-blue-50' : 'hover:bg-gray-50'}>
                            <TableCell>
                              <Badge variant={
                                pricing.careerStage === 'Emerging' ? 'secondary' :
                                pricing.careerStage.includes('Established') ? 'default' :
                                pricing.careerStage.includes('Mainstream') ? 'destructive' :
                                'outline'
                              }>
                                {pricing.careerStage}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              {isEditing ? (
                                <Input
                                  type="number"
                                  value={currentValues.avgCapacity || 0}
                                  onChange={(e) => updateGaEditingValue('avgCapacity', parseInt(e.target.value) || 0)}
                                  className="w-24 text-center font-mono text-sm"
                                />
                              ) : (
                                <span className="font-mono text-sm">{pricing.avgCapacity.toLocaleString()}</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center bg-green-50">
                              {isEditing ? (
                                <Input
                                  type="number"
                                  value={Math.round((currentValues.price1Show || 0) / 100)}
                                  onChange={(e) => updateGaEditingValue('price1Show', parseInt(e.target.value) * 100 || 0)}
                                  className="w-24 text-center font-mono text-sm"
                                />
                              ) : (
                                <span className="font-mono text-sm font-semibold">
                                  ${(pricing.price1Show / 100).toLocaleString()}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-center bg-green-100">
                              {isEditing ? (
                                <Input
                                  type="number"
                                  value={Math.round((currentValues.price2Shows || 0) / 100)}
                                  onChange={(e) => updateGaEditingValue('price2Shows', parseInt(e.target.value) * 100 || 0)}
                                  className="w-24 text-center font-mono text-sm"
                                />
                              ) : (
                                <span className="font-mono text-sm font-semibold">
                                  ${(pricing.price2Shows / 100).toLocaleString()}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-center bg-green-200">
                              {isEditing ? (
                                <Input
                                  type="number"
                                  value={Math.round((currentValues.price3Shows || 0) / 100)}
                                  onChange={(e) => updateGaEditingValue('price3Shows', parseInt(e.target.value) * 100 || 0)}
                                  className="w-24 text-center font-mono text-sm"
                                />
                              ) : (
                                <span className="font-mono text-sm font-semibold">
                                  ${(pricing.price3Shows / 100).toLocaleString()}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-center bg-green-300">
                              {isEditing ? (
                                <Input
                                  type="number"
                                  value={Math.round((currentValues.price4Shows || 0) / 100)}
                                  onChange={(e) => updateGaEditingValue('price4Shows', parseInt(e.target.value) * 100 || 0)}
                                  className="w-24 text-center font-mono text-sm"
                                />
                              ) : (
                                <span className="font-mono text-sm font-semibold">
                                  ${(pricing.price4Shows / 100).toLocaleString()}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-center bg-green-400">
                              {isEditing ? (
                                <Input
                                  type="number"
                                  value={Math.round((currentValues.price5PlusShows || 0) / 100)}
                                  onChange={(e) => updateGaEditingValue('price5PlusShows', parseInt(e.target.value) * 100 || 0)}
                                  className="w-24 text-center font-mono text-sm"
                                />
                              ) : (
                                <span className="font-mono text-sm font-semibold">
                                  ${(pricing.price5PlusShows / 100).toLocaleString()}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              {isEditing ? (
                                <div className="flex gap-2 justify-center">
                                  <Button
                                    variant="default"
                                    size="sm"
                                    onClick={handleSaveGaPricing}
                                    className="h-8 w-8 p-0"
                                  >
                                    <Save className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleCancelGaEdit}
                                    className="h-8 w-8 p-0"
                                  >
                                    ✕
                                  </Button>
                                </div>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditGaPricing(pricing.careerStage, pricing)}
                                >
                                  <Edit3 className="h-4 w-4" />
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h4 className="font-medium text-amber-900 mb-2">Pricing Examples</h4>
                  <div className="text-sm text-amber-800 space-y-2">
                    <div><strong>Example 1:</strong> Emerging artist, 1 show = $899 total</div>
                    <div><strong>Example 2:</strong> Emerging artist, 3 shows = $699 × 3 = $2,097 total</div>
                    <div><strong>Example 3:</strong> Mainstream 1, 5 shows = $3,599 × 5 = $17,995 total</div>
                    <div><strong>Example 4:</strong> Mainstream 1, 10 shows = $3,599 × 10 = $35,990 total</div>
                    <p className="mt-3 text-xs">
                      Note: All values are stored in cents in the database but displayed as dollars in the UI.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'vip-pricing' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              SET Audio Pricing Table
            </CardTitle>
            <CardDescription>
              Formula-based SET Audio pricing using sublinear curve: (COGS × # shows^exponent) × Package Markup
            </CardDescription>
          </CardHeader>
          <CardContent>
            {vipPricingLoading ? (
              <div className="animate-pulse space-y-3">
                <div className="h-10 bg-gray-200 rounded"></div>
                <div className="h-10 bg-gray-200 rounded"></div>
                <div className="h-10 bg-gray-200 rounded"></div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-2">SET Audio Pricing Structure</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• <strong>Formula-Based:</strong> SET Audio uses the sublinear curve formula for volume pricing</li>
                    <li>• <strong>Artist COGS:</strong> Base cost for artist services (in cents in database)</li>
                    <li>• <strong>Exponent:</strong> Volume discount factor - lower values give steeper discounts</li>
                    <li>• <strong>Package Markup:</strong> Multiplier applied to final calculation (typically 1.5x)</li>
                    <li>• <strong>Min Cost/Show:</strong> Minimum price per show enforced for all calculations</li>
                  </ul>
                </div>

                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[180px]">Career Stage</TableHead>
                        <TableHead className="text-center">Avg Capacity</TableHead>
                        <TableHead className="text-center bg-purple-50">Artist COGS</TableHead>
                        <TableHead className="text-center bg-purple-100">Exponent</TableHead>
                        <TableHead className="text-center bg-purple-200">Package Markup</TableHead>
                        <TableHead className="text-center bg-purple-300">Min Cost/Show</TableHead>
                        <TableHead className="text-center w-[100px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {vipPricingData.map((pricing) => {
                        const isEditing = editingVipPricing === pricing.careerStage;
                        const currentValues = isEditing ? editingVipValues : pricing;

                        return (
                          <TableRow key={pricing.careerStage} className={isEditing ? 'bg-blue-50' : 'hover:bg-gray-50'}>
                            <TableCell>
                              <Badge variant={
                                pricing.careerStage === 'Emerging' ? 'secondary' :
                                pricing.careerStage === 'Developing' ? 'outline' :
                                pricing.careerStage.includes('Established') ? 'default' :
                                pricing.careerStage.includes('Mainstream') ? 'destructive' :
                                'outline'
                              }>
                                {pricing.careerStage}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              {isEditing ? (
                                <Input
                                  type="number"
                                  value={currentValues.avgCapacity || 0}
                                  onChange={(e) => updateVipEditingValue('avgCapacity', parseInt(e.target.value) || 0)}
                                  className="w-24 text-center font-mono text-sm"
                                />
                              ) : (
                                <span className="font-mono text-sm">{pricing.avgCapacity.toLocaleString()}</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center bg-purple-50">
                              {isEditing ? (
                                <Input
                                  type="number"
                                  value={Math.round((currentValues.artistCogs || 0) / 100)}
                                  onChange={(e) => updateVipEditingValue('artistCogs', parseInt(e.target.value) * 100 || 0)}
                                  className="w-24 text-center font-mono text-sm"
                                />
                              ) : (
                                <span className="font-mono text-sm font-semibold">
                                  ${(pricing.artistCogs / 100).toLocaleString()}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-center bg-purple-100">
                              {isEditing ? (
                                <Input
                                  type="number"
                                  step="0.0000001"
                                  value={currentValues.exponent || ''}
                                  onChange={(e) => updateVipEditingValue('exponent', e.target.value)}
                                  className="w-32 text-center font-mono text-sm"
                                />
                              ) : (
                                <span className="font-mono text-sm font-semibold">
                                  {pricing.exponent}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-center bg-purple-200">
                              {isEditing ? (
                                <Input
                                  type="number"
                                  step="0.001"
                                  value={currentValues.packageMarkup || ''}
                                  onChange={(e) => updateVipEditingValue('packageMarkup', e.target.value)}
                                  className="w-24 text-center font-mono text-sm"
                                />
                              ) : (
                                <span className="font-mono text-sm font-semibold">
                                  {pricing.packageMarkup}×
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-center bg-purple-300">
                              {isEditing ? (
                                <Input
                                  type="number"
                                  value={Math.round((currentValues.minCostPerShow || 0) / 100)}
                                  onChange={(e) => updateVipEditingValue('minCostPerShow', parseInt(e.target.value) * 100 || 0)}
                                  className="w-24 text-center font-mono text-sm"
                                />
                              ) : (
                                <span className="font-mono text-sm font-semibold">
                                  ${(pricing.minCostPerShow / 100).toLocaleString()}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              {isEditing ? (
                                <div className="flex gap-2 justify-center">
                                  <Button
                                    variant="default"
                                    size="sm"
                                    onClick={handleSaveVipPricing}
                                    className="h-8 w-8 p-0"
                                  >
                                    <Save className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleCancelVipEdit}
                                    className="h-8 w-8 p-0"
                                  >
                                    ✕
                                  </Button>
                                </div>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditVipPricing(pricing.careerStage, pricing)}
                                >
                                  <Edit3 className="h-4 w-4" />
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h4 className="font-medium text-amber-900 mb-2">Pricing Examples</h4>
                  <div className="text-sm text-amber-800 space-y-2">
                    <div><strong>Formula:</strong> (Artist COGS × # shows^exponent) × Package Markup, with min cost enforcement</div>
                    <div><strong>Example 1:</strong> Emerging artist, 1 show = $1,764 × 1.5 = $2,646 (or $749 min)</div>
                    <div><strong>Example 2:</strong> Emerging artist, 3 shows = ($1,764 × 3^0.443) × 1.5 ≈ $3,951 (or $2,247 min)</div>
                    <div><strong>Example 3:</strong> Mainstream 1, 5 shows = ($9,314 × 5^0.665) × 1.5 ≈ $42,000 (or $22,995 min)</div>
                    <p className="mt-3 text-xs">
                      Note: All COGS and Min Cost values are stored in cents in the database but displayed as dollars in the UI.
                      Exponent and Package Markup are stored as decimals.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'allaccess-pricing' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              SET Video Pricing Table
            </CardTitle>
            <CardDescription>
              Formula-based SET Video pricing using sublinear curve: (COGS × # shows^exponent) × Package Markup (2.875)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {allAccessPricingLoading ? (
              <div className="animate-pulse space-y-3">
                <div className="h-10 bg-gray-200 rounded"></div>
                <div className="h-10 bg-gray-200 rounded"></div>
                <div className="h-10 bg-gray-200 rounded"></div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <h4 className="font-medium text-orange-900 mb-2">SET Video Pricing Structure</h4>
                  <ul className="text-sm text-orange-700 space-y-1">
                    <li>• <strong>Formula-Based:</strong> SET Video uses the sublinear curve formula for volume pricing</li>
                    <li>• <strong>Artist COGS:</strong> Base cost for artist services (in cents in database)</li>
                    <li>• <strong>Exponent:</strong> Volume discount factor - lower values give steeper discounts</li>
                    <li>• <strong>Package Markup:</strong> Multiplier applied to final calculation (2.875x for All-Access)</li>
                    <li>• <strong>Min Cost/Show:</strong> Minimum price per show enforced for all calculations</li>
                  </ul>
                </div>

                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[180px]">Career Stage</TableHead>
                        <TableHead className="text-center">Avg Capacity</TableHead>
                        <TableHead className="text-center bg-orange-50">Artist COGS</TableHead>
                        <TableHead className="text-center bg-orange-100">Exponent</TableHead>
                        <TableHead className="text-center bg-orange-200">Package Markup</TableHead>
                        <TableHead className="text-center bg-orange-300">Min Cost/Show</TableHead>
                        <TableHead className="text-center w-[100px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {allAccessPricingData.map((pricing) => {
                        const isEditing = editingAllAccessPricing === pricing.careerStage;
                        const currentValues = isEditing ? editingAllAccessValues : pricing;

                        return (
                          <TableRow key={pricing.careerStage} className={isEditing ? 'bg-orange-50' : 'hover:bg-gray-50'}>
                            <TableCell>
                              <Badge variant={
                                pricing.careerStage === 'Emerging' ? 'secondary' :
                                pricing.careerStage === 'Developing' ? 'outline' :
                                pricing.careerStage.includes('Established') ? 'default' :
                                pricing.careerStage.includes('Mainstream') ? 'destructive' :
                                'outline'
                              }>
                                {pricing.careerStage}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              {isEditing ? (
                                <Input
                                  type="number"
                                  value={currentValues.avgCapacity || 0}
                                  onChange={(e) => updateAllAccessEditingValue('avgCapacity', parseInt(e.target.value) || 0)}
                                  className="w-24 text-center font-mono text-sm"
                                />
                              ) : (
                                <span className="font-mono text-sm">{pricing.avgCapacity.toLocaleString()}</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center bg-orange-50">
                              {isEditing ? (
                                <Input
                                  type="number"
                                  value={Math.round((currentValues.artistCogs || 0) / 100)}
                                  onChange={(e) => updateAllAccessEditingValue('artistCogs', parseInt(e.target.value) * 100 || 0)}
                                  className="w-24 text-center font-mono text-sm"
                                />
                              ) : (
                                <span className="font-mono text-sm font-semibold">
                                  ${(pricing.artistCogs / 100).toLocaleString()}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-center bg-orange-100">
                              {isEditing ? (
                                <Input
                                  type="number"
                                  step="0.0000001"
                                  value={currentValues.exponent || ''}
                                  onChange={(e) => updateAllAccessEditingValue('exponent', e.target.value)}
                                  className="w-32 text-center font-mono text-sm"
                                />
                              ) : (
                                <span className="font-mono text-sm font-semibold">
                                  {pricing.exponent}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-center bg-orange-200">
                              {isEditing ? (
                                <Input
                                  type="number"
                                  step="0.001"
                                  value={currentValues.packageMarkup || ''}
                                  onChange={(e) => updateAllAccessEditingValue('packageMarkup', e.target.value)}
                                  className="w-24 text-center font-mono text-sm"
                                />
                              ) : (
                                <span className="font-mono text-sm font-semibold">
                                  {pricing.packageMarkup}×
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-center bg-orange-300">
                              {isEditing ? (
                                <Input
                                  type="number"
                                  value={Math.round((currentValues.minCostPerShow || 0) / 100)}
                                  onChange={(e) => updateAllAccessEditingValue('minCostPerShow', parseInt(e.target.value) * 100 || 0)}
                                  className="w-24 text-center font-mono text-sm"
                                />
                              ) : (
                                <span className="font-mono text-sm font-semibold">
                                  ${(pricing.minCostPerShow / 100).toLocaleString()}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              {isEditing ? (
                                <div className="flex gap-2 justify-center">
                                  <Button
                                    variant="default"
                                    size="sm"
                                    onClick={handleSaveAllAccessPricing}
                                    className="h-8 w-8 p-0"
                                  >
                                    <Save className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleCancelAllAccessEdit}
                                    className="h-8 w-8 p-0"
                                  >
                                    ✕
                                  </Button>
                                </div>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditAllAccessPricing(pricing.careerStage, pricing)}
                                >
                                  <Edit3 className="h-4 w-4" />
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h4 className="font-medium text-amber-900 mb-2">Pricing Examples</h4>
                  <div className="text-sm text-amber-800 space-y-2">
                    <div><strong>Formula:</strong> (Artist COGS × # shows^exponent) × Package Markup (2.875), with min cost enforcement</div>
                    <div><strong>Example 1:</strong> Emerging artist, 1 show = $1,764 × 2.875 = $5,071 (or $999 min)</div>
                    <div><strong>Example 2:</strong> Emerging artist, 3 shows = ($1,764 × 3^0.443) × 2.875 ≈ $7,570 (or $2,997 min)</div>
                    <div><strong>Example 3:</strong> Mainstream 1, 5 shows = ($9,314 × 5^0.665) × 2.875 ≈ $80,540 (or $39,995 min)</div>
                    <p className="mt-3 text-xs">
                      Note: All COGS and Min Cost values are stored in cents in the database but displayed as dollars in the UI.
                      Exponent and Package Markup are stored as decimals.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'artist-cogs-old' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Awareness Settings */}
          <Card>
            <CardHeader>
              <CardTitle>Awareness Settings</CardTitle>
              <CardDescription>CPM and impression multipliers</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>CPM</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={mediaSettings.awareness.cpm}
                  onChange={(e) => updateMediaSetting('awareness.cpm', parseFloat(e.target.value) || 0)}
                />
              </div>
              <Separator />
              <div className="space-y-2">
                <Label>Small Market Multiplier</Label>
                <Input
                  type="number"
                  value={mediaSettings.awareness.impressionMultipliers.small}
                  onChange={(e) => updateMediaSetting('awareness.impressionMultipliers.small', parseInt(e.target.value) || 0)}
                />
              </div>
              <div className="space-y-2">
                <Label>Medium Market Multiplier</Label>
                <Input
                  type="number"
                  value={mediaSettings.awareness.impressionMultipliers.medium}
                  onChange={(e) => updateMediaSetting('awareness.impressionMultipliers.medium', parseInt(e.target.value) || 0)}
                />
              </div>
              <div className="space-y-2">
                <Label>Large Market Multiplier</Label>
                <Input
                  type="number"
                  value={mediaSettings.awareness.impressionMultipliers.large}
                  onChange={(e) => updateMediaSetting('awareness.impressionMultipliers.large', parseInt(e.target.value) || 0)}
                />
              </div>
              <div className="space-y-2">
                <Label>National Tour Multiplier</Label>
                <Input
                  type="number"
                  value={mediaSettings.awareness.impressionMultipliers.nationalTour}
                  onChange={(e) => updateMediaSetting('awareness.impressionMultipliers.nationalTour', parseInt(e.target.value) || 0)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Engagement Settings */}
          <Card>
            <CardHeader>
              <CardTitle>Engagement Settings</CardTitle>
              <CardDescription>Base CPM and market multipliers</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Base CPM</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={mediaSettings.engagement.baseCPM}
                  onChange={(e) => updateMediaSetting('engagement.baseCPM', parseFloat(e.target.value) || 0)}
                />
              </div>
              <Separator />
              <div className="space-y-2">
                <Label>Small Market Multiplier</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={mediaSettings.engagement.marketMultipliers.small}
                  onChange={(e) => updateMediaSetting('engagement.marketMultipliers.small', parseFloat(e.target.value) || 0)}
                />
              </div>
              <div className="space-y-2">
                <Label>Medium Market Multiplier</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={mediaSettings.engagement.marketMultipliers.medium}
                  onChange={(e) => updateMediaSetting('engagement.marketMultipliers.medium', parseFloat(e.target.value) || 0)}
                />
              </div>
              <div className="space-y-2">
                <Label>Large Market Multiplier</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={mediaSettings.engagement.marketMultipliers.large}
                  onChange={(e) => updateMediaSetting('engagement.marketMultipliers.large', parseFloat(e.target.value) || 0)}
                />
              </div>
              <div className="space-y-2">
                <Label>National Tour Multiplier</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={mediaSettings.engagement.marketMultipliers.nationalTour}
                  onChange={(e) => updateMediaSetting('engagement.marketMultipliers.nationalTour', parseFloat(e.target.value) || 0)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Leads Settings */}
          <Card>
            <CardHeader>
              <CardTitle>Leads Settings</CardTitle>
              <CardDescription>Base CPM and market multipliers</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Base CPM</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={mediaSettings.leads.baseCPM}
                  onChange={(e) => updateMediaSetting('leads.baseCPM', parseFloat(e.target.value) || 0)}
                />
              </div>
              <Separator />
              <div className="space-y-2">
                <Label>Small Market Multiplier</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={mediaSettings.leads.marketMultipliers.small}
                  onChange={(e) => updateMediaSetting('leads.marketMultipliers.small', parseFloat(e.target.value) || 0)}
                />
              </div>
              <div className="space-y-2">
                <Label>Medium Market Multiplier</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={mediaSettings.leads.marketMultipliers.medium}
                  onChange={(e) => updateMediaSetting('leads.marketMultipliers.medium', parseFloat(e.target.value) || 0)}
                />
              </div>
              <div className="space-y-2">
                <Label>Large Market Multiplier</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={mediaSettings.leads.marketMultipliers.large}
                  onChange={(e) => updateMediaSetting('leads.marketMultipliers.large', parseFloat(e.target.value) || 0)}
                />
              </div>
              <div className="space-y-2">
                <Label>National Tour Multiplier</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={mediaSettings.leads.marketMultipliers.nationalTour}
                  onChange={(e) => updateMediaSetting('leads.marketMultipliers.nationalTour', parseFloat(e.target.value) || 0)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Lead Divisors */}
          <Card className="md:col-span-2 lg:col-span-3">
            <CardHeader>
              <CardTitle>Lead Conversion Divisors</CardTitle>
              <CardDescription>Divisors for calculating lead generation by career stage</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium mb-3">Low Lead Divisors</h4>
                  <div className="space-y-2">
                    {Object.entries(mediaSettings.leads.leadDivisors.low).map(([stage, divisor]) => (
                      <div key={stage} className="flex items-center gap-2">
                        <Label className="min-w-[120px]">{stage}</Label>
                        <Input
                          type="number"
                          value={divisor}
                          onChange={(e) => updateMediaSetting(`leads.leadDivisors.low.${stage}`, parseInt(e.target.value) || 0)}
                          className="w-20"
                        />
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="font-medium mb-3">High Lead Divisors</h4>
                  <div className="space-y-2">
                    {Object.entries(mediaSettings.leads.leadDivisors.high).map(([stage, divisor]) => (
                      <div key={stage} className="flex items-center gap-2">
                        <Label className="min-w-[120px]">{stage}</Label>
                        <Input
                          type="number"
                          value={divisor}
                          onChange={(e) => updateMediaSetting(`leads.leadDivisors.high.${stage}`, parseInt(e.target.value) || 0)}
                          className="w-20"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* TODO: Implement parameters tab - currently incomplete */}
      {/* {activeTab === 'parameters' && (
        <Card>
          <CardHeader>
            <CardTitle>Calculator Parameters</CardTitle>
            <CardDescription>Manage individual calculation parameters</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Input
                placeholder="Category"
                value={newParameter.category || ''}
                onChange={(e) => setNewParameter(prev => ({ ...prev, category: e.target.value }))}
              />
              <Input
                placeholder="Name"
                value={newParameter.name || ''}
                onChange={(e) => setNewParameter(prev => ({ ...prev, name: e.target.value }))}
              />
              <Input
                placeholder="Value"
                value={newParameter.value || ''}
                onChange={(e) => setNewParameter(prev => ({ ...prev, value: e.target.value }))}
              />
              <Button onClick={() => createParameterMutation.mutate(newParameter)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Parameter
              </Button>
            </div>

            <div className="space-y-2">
              {parameters.map((param) => (
                <div key={param.id} className="flex items-center gap-2 p-3 border rounded-lg">
                  <Badge variant="secondary">{param.category}</Badge>
                  <span className="font-medium">{param.name}</span>
                  <span className="text-gray-600">{param.value}</span>
                  <div className="ml-auto flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditingParameter(param)}
                    >
                      <Edit3 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )} */}

      </div>
    </div>
  );
}